import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment as env } from '../../environments/environment';
import { ApiAuthHttpClient } from '../http/ApiAuthHttpClient';
import { ProductImagesBespoke } from '../shared/model/product-images-bespoke.model';
import { ProductPricing } from '../shared/model/product-pricing.model';
import { ProductType } from '../shared/model/product-type.model';
import { ProductVariableGroup } from '../shared/model/product-variable-group.model';
import { ProductVariableOption } from '../shared/model/product-variable-option.model';
import { ProductVariable } from '../shared/model/product-variable.model';
import { Store } from '../store';
import { ApiService } from './api.service';

interface ProductTypesQueryParams {
    status?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ProductsService {
    constructor(private apiService: ApiService, private store: Store, private http: ApiAuthHttpClient) { }

    create(product) {
        const user = this.store.selectForLocal('user');
        product.createdBy = user;
        const dataToSend = {
            product,
        };

        return this.http.post(env.apiPath + 'products/create', product, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    generateBespokePricingResults(itemToPrice) {
        const dataToSend = {
            itemToPrice
        }
        return this.http.post(env.apiPath + 'pricing/bespoke', dataToSend).pipe(map(({ data }: any) => data));
    }

    priceProductSku(itemsToPrice) {
        const dataToSend = {
            itemsToPrice
        }
        return this.http.post(env.apiPath + 'pricing/sku', dataToSend)
            .pipe(map(({ data }: any) => data));
    }

    update(product) {
        const user = this.store.selectForLocal('user');
        product.createdBy = user;
        const dataToSend = {
            product,
        };

        return this.http.put(env.apiPath + 'products/' + product.id, product, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    updateStatus(productCode, status) {
        const user = this.store.selectForLocal('user');

        const dataToSend = {
            productCode,
            status,
        };

        return this.http.post(env.apiPath + 'products/status', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    updateFreeDelivery(productCode, allowFreeDelivery) {
        const user = this.store.selectForLocal('user');

        const dataToSend = {
            productCode,
            allowFreeDelivery,
        };

        return this.http.post(env.apiPath + 'products/freedelivery', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    updateSpecialOffer(productCode, specialOffer) {
        const user = this.store.selectForLocal('user');

        const dataToSend = {
            productCode,
            specialOffer,
            user,
        };

        return this.http.put(env.apiPath + 'products/specialOffer', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getBySlug(slug) {
        return this.http.get(env.apiPath + 'products/' + slug, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }
    productsByTypeRangeFinish(productType, productRange, productFinish) {
        return this.http
            .get(
                env.apiPath + 'products/type/' + productType + '/range/' + productRange + '/finish/' + productFinish,
                this.apiService.getHttpOptions()
            )
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    findAll() {
        return this.http.get(env.apiPath + 'products/all', this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getProductDetails(slug) {
        return this.http.get(env.apiPath + 'products/public/' + slug, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    search(params) {
        const dataToSend = {
            params,
        };
        return this.http.post(env.apiPath + 'products/search', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    URLDupecheck(slug, productCode) {
        const dataToSend = {
            slug,
            productCode,
        };
        return this.http.post(env.apiPath + 'products/dupecheckURL', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getProductVariables(): Observable<ProductVariable[]> {
        return this.http
            .get(env.apiPath + 'product-variables', this.apiService.getHttpOptions())
            .pipe(map((data: any) => data.data, catchError(this.apiService.handleError)));
    }

    getProductVariablesAll() {
        return this.http.get(`${env.apiPath}product-variables/all`, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data),
            catchError(this.apiService.handleError)
        );
    }

    getProductVariableById(id: string): Observable<ProductVariable> {
        return this.http.get(`${env.apiPath}product-variables/${id}`, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    createProductVariable(payload: ProductVariable): Observable<ProductVariable> {
        return this.http.post(`${env.apiPath}product-variables`, payload, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    updateProductVariable(id: number, payload: ProductVariable): Observable<ProductVariable> {
        return this.http.put(`${env.apiPath}product-variables/${id}`, payload, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    deleteProductVariable(id: string): Observable<void> {
        return this.http.delete(`${env.apiPath}product-variables/${id}`, this.apiService.getHttpOptions()).pipe(
            map(() => null),
            catchError(this.apiService.handleError)
        );
    }

    getProductTypes(params?: ProductTypesQueryParams) {
        return this.http
            .get(env.apiPath + 'product-types', {
                ...this.apiService.getHttpOptions(),
                params: { ...params },
            })
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    getProductTypeById(id: string): Observable<ProductType> {
        return this.http.get(`${env.apiPath}product-types/${id}`, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    createProductType(payload: ProductType): Observable<ProductType> {
        return this.http.post(`${env.apiPath}product-types`, payload, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    updateProductType(id: string, payload: ProductType): Observable<ProductType> {
        return this.http.put(`${env.apiPath}product-types/${id}`, payload, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    deleteProductType(id: string): Observable<void> {
        return this.http.delete(`${env.apiPath}product-types/${id}`, this.apiService.getHttpOptions()).pipe(
            map(() => null),
            catchError(this.apiService.handleError)
        );
    }

    getRangesByProductType(productType) {
        return this.http
            .get(env.apiPath + 'product-range/product-type/' + productType, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    getProductsByRange(productRange) {
        return this.http.get(env.apiPath + 'products/range/' + productRange, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getProductVariableOptionsByGroupSupplier(id, supplierId) {
        return this.http
            .get(
                env.apiPath + 'product-variable-options/variable-group/' + id + '/' + supplierId,
                this.apiService.getHttpOptions()
            )
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    getRangesByProductTypeOption(productType, optionId) {
        return this.http
            .get(
                env.apiPath + 'product-range/product-type/' + productType + '/option/' + optionId,
                this.apiService.getHttpOptions()
            )
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    getProductVariablesWithOptions(supplierId, rangeId, productType, textureType) {
        return this.http
            .get(
                env.apiPath +
                'product-variables/options/' +
                supplierId +
                '/' +
                rangeId +
                '/' +
                productType +
                '/' +
                textureType,
                this.apiService.getHttpOptions()
            )
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    getProductGroups() {
        return this.http.get(env.apiPath + 'product-groups').pipe(map((data: any) => data));
    }

    getProductGroupsFlat() {
        return this.http.get(env.apiPath + 'product-groups/flat', this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getProductGroupBySlug(slug) {
        return this.http.get(env.apiPath + 'product-groups/' + slug, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    createProductGroup(productGroup) {
        const dataToSend = {
            productGroup,
        };

        return this.http.post(env.apiPath + 'product-groups/', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    updateProductGroup(productGroup) {
        const dataToSend = {
            productGroup,
        };

        return this.http.put(env.apiPath + 'product-groups/', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getProductRanges() {
        return this.http.get(env.apiPath + 'product-range', this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getProductRangeBySlug(slug) {
        return this.http.get(env.apiPath + 'product-range/' + slug, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getProductRangesBySupplier(supplierId) {
        return this.http
            .get(env.apiPath + 'product-range/supplier/' + supplierId, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    createProductRange(productRange) {
        const dataToSend = {
            productRange,
        };

        return this.http.post(env.apiPath + 'product-range/', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    updateProductRange(productRange) {
        const dataToSend = {
            productRange,
        };

        return this.http.put(env.apiPath + 'product-range/', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    deleteProductGroup(id) {
        return this.http.delete(env.apiPath + 'product-groups/' + id, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    deleteProductRange(id) {
        return this.http.delete(env.apiPath + 'product-range/' + id, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    loadBespokeConfigurationPublic(productRange, productType, supplierId) {
        const dataToSend = {
            productRange,
            productType,
            supplierId,
        };

        return this.http
            .post(
                env.apiPath + 'product-range/product-type/configuration/public',
                dataToSend,
                this.apiService.getHttpOptions()
            )
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    asyncLoadBespokeConfiguration(productRange, productType, supplierId) {
        const dataToSend = {
            productRange,
            productType,
            supplierId,
        };

        return this.http
            .post(
                env.apiPath + 'product-range/product-type/configuration',
                dataToSend,
                this.apiService.getHttpOptions()
            )
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    loadBespokeConfiguration(productRange, productType, supplierId) {
        const dataToSend = {
            productRange,
            supplierId,
            productType,
        };

        return this.http
            .post(env.apiPath + 'product-range/product-type/configuration', dataToSend)
            .pipe(map((data: any) => data));
    }

    saveBespokeConfiguration(productRange, productType, textures) {
        const dataToSend = {
            productRange,
            productType,
            textures,
        };

        return this.http
            .put(env.apiPath + 'product-range/product-type/configuration', dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    getProductVariableOptionsByGroup(id): Observable<ProductVariableOption[]> {
        return this.http
            .get(env.apiPath + 'product-variable-options/variable-group/' + id, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => data.data),
                catchError(this.apiService.handleError)
            );
    }

    getProductVariableOptionsById(id): Observable<ProductVariableOption> {
        return this.http.get(env.apiPath + 'product-variable-options/' + id, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    getProductVariableOptions(): Observable<Partial<ProductVariableOption>[]> {
        return this.http.get(env.apiPath + 'product-variable-options', this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    getProductVariableOptionsFinishes(): Observable<Partial<ProductVariableOption>[]> {
        return this.http
            .get(env.apiPath + 'product-variable-options/variable-group/finishes', this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => data.data),
                catchError(this.apiService.handleError)
            );
    }

    getProductProductImagesBespoke(): Observable<ProductImagesBespoke[]> {
        return this.http.get(env.apiPath + 'product-images-bespoke', this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    createProductProductImagesBespoke(payload: ProductImagesBespoke): Observable<void> {
        return this.http.post(env.apiPath + 'product-images-bespoke', payload, this.apiService.getHttpOptions()).pipe(
            map(() => null),
            catchError(this.apiService.handleError)
        );
    }

    updateProductProductImagesBespoke(id: number, payload: Partial<ProductImagesBespoke>): Observable<void> {
        return this.http
            .put(env.apiPath + 'product-images-bespoke/' + id, payload, this.apiService.getHttpOptions())
            .pipe(
                map(() => null),
                catchError(this.apiService.handleError)
            );
    }

    deleteProductProductImagesBespoke(id: number): Observable<void> {
        return this.http.delete(env.apiPath + 'product-images-bespoke/' + id, this.apiService.getHttpOptions()).pipe(
            map(() => null),
            catchError(this.apiService.handleError)
        );
    }

    //
    getProductPricingRules(): Observable<ProductPricing[]> {
        return this.http.get(env.apiPath + 'product-pricing-rules', this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    createProductPricingRule(payload: ProductPricing): Observable<void> {
        return this.http.post(env.apiPath + 'product-pricing-rules', payload, this.apiService.getHttpOptions()).pipe(
            map(() => null),
            catchError(this.apiService.handleError)
        );
    }

    updateProductPricingRule(id: number, payload: Partial<ProductPricing>): Observable<void> {
        return this.http
            .put(env.apiPath + 'product-pricing-rules/' + id, payload, this.apiService.getHttpOptions())
            .pipe(
                map(() => null),
                catchError(this.apiService.handleError)
            );
    }

    deleteProductPricingRule(id: number): Observable<void> {
        return this.http.delete(env.apiPath + 'product-pricing-rules/' + id, this.apiService.getHttpOptions()).pipe(
            map(() => null),
            catchError(this.apiService.handleError)
        );
    }

    getSupplierRateSummary(params: any = {}): Observable<any> {
        return this.http
            .get(env.apiPath + 'pricing/supplier-rates/summary', {
                ...this.apiService.getHttpOptions(),
                params,
            })
            .pipe(
                map((data: any) => data.data),
                catchError(this.apiService.handleError)
            );
    }

    getSupplierRateObservations(params: any = {}): Observable<any> {
        return this.http
            .get(env.apiPath + 'pricing/supplier-rates/observations', {
                ...this.apiService.getHttpOptions(),
                params,
            })
            .pipe(
                map((data: any) => data.data),
                catchError(this.apiService.handleError)
            );
    }

    getSupplierPriceRecommendations(params: any = {}): Observable<any> {
        return this.http
            .get(env.apiPath + 'pricing/supplier-rates/recommendations', {
                ...this.apiService.getHttpOptions(),
                params,
            })
            .pipe(
                map((data: any) => data.data),
                catchError(this.apiService.handleError)
            );
    }

    getSupplierRateVersions(params: any = {}): Observable<any> {
        return this.http
            .get(env.apiPath + 'pricing/supplier-rates/versions', {
                ...this.apiService.getHttpOptions(),
                params,
            })
            .pipe(
                map((data: any) => data.data),
                catchError(this.apiService.handleError)
            );
    }

    approveSupplierRateVersion(id: string, payload: any = {}): Observable<any> {
        return this.http
            .post(env.apiPath + 'pricing/supplier-rates/versions/' + id + '/approve', payload, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => data.data),
                catchError(this.apiService.handleError)
            );
    }

    rejectSupplierRateVersion(id: string, payload: any = {}): Observable<any> {
        return this.http
            .post(env.apiPath + 'pricing/supplier-rates/versions/' + id + '/reject', payload, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => data.data),
                catchError(this.apiService.handleError)
            );
    }

    //

    createProductVariableOption(payload: ProductVariableOption): Observable<ProductVariableOption> {
        return this.http.post(`${env.apiPath}product-variable-options`, payload, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data),
            catchError(this.apiService.handleError)
        );
    }

    updateProductVariableOption(id: number, payload: ProductVariableOption): Observable<ProductVariableOption> {
        return this.http
            .put(`${env.apiPath}product-variable-options/${id}`, payload, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => data),
                catchError(this.apiService.handleError)
            );
    }

    deleteProductVariableOption(id: number): Observable<void> {
        return this.http.delete(`${env.apiPath}product-variable-options/${id}`, this.apiService.getHttpOptions()).pipe(
            map(() => null),
            catchError(this.apiService.handleError)
        );
    }

    getProductVariableGroups(): Observable<ProductVariableGroup[]> {
        return this.http.get(`${env.apiPath}product-variable-groups`, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data.data),
            catchError(this.apiService.handleError)
        );
    }

    getBaseItems(supplierId) {
        return this.http.get(env.apiPath + 'products/baseItems/' + supplierId, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    setBaseItems(baseItems, supplierId) {
        const dataToSend = {
            supplierId,
            baseItems,
        };
        return this.http
            .put(env.apiPath + 'products/baseItems/' + supplierId, dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    getSupplierOptionsPricing(supplierId) {
        return this.http
            .get(env.apiPath + 'products/supplierPricing/' + supplierId, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    setSupplierOptionsPricing(pricing, supplierId) {
        const dataToSend = {
            pricing,
            supplierId,
        };
        return this.http
            .put(env.apiPath + 'products/supplierPricing/' + supplierId, dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    getLeadtimes(productId) {
        return this.http.get(env.apiPath + 'products/leadtimes/' + productId, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    delete(productId) {
        return this.http.delete(env.apiPath + 'products/' + productId, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }
}
